import React from 'react';
import { Link } from 'react-router-dom';
import { Target, ArrowLeft, Shield } from 'lucide-react';
import SEOHead from '@/components/SEOHead';

const PrivacyPage = () => {
  const sections = [
    {
      title: '1. Information We Collect',
      content: [
        'Account Information: When you sign up, we collect your name, email address, and authentication details. If you use Google sign-in, we receive your basic profile information from Google.',
        'Assessment Data: The answers you provide during the career assessment (education level, skills, goals, interests) are stored to generate your personalised career recommendations.',
        'Lead Form Data: If you fill out a lead capture form, we collect your name, phone number, email, city, education level, and future career plans.',
        'Skill2Income Data: Your inputs to the Skill2Income tool (education, skills, target income, timeline, work preference) are stored to generate and retrieve your income analysis.',
        'Usage Data: We collect basic usage information such as pages visited and features used, to improve our platform.',
      ],
    },
    {
      title: '2. How We Use Your Information',
      content: [
        'To provide personalised career guidance and AI-generated recommendations.',
        'To generate income roadmaps, skill assessments, and career path analyses.',
        'To improve and enhance our platform features based on usage patterns.',
        'To communicate with you about your account or enquiries (only when you contact us).',
        'We do not sell your personal information to third parties.',
      ],
    },
    {
      title: '3. Data Storage & Protection',
      content: [
        'Your data is stored securely in our database with encryption in transit.',
        'Passwords are hashed using industry-standard bcrypt encryption and are never stored in plain text.',
        'We use JWT (JSON Web Tokens) for secure session management.',
        'Access to user data is restricted to authorised administrators only.',
      ],
    },
    {
      title: '4. Data Retention',
      content: [
        'Account data is retained for as long as your account is active.',
        'Assessment and Skill2Income results are retained to allow you to revisit your recommendations.',
        'Lead form submissions are retained for the purpose of providing career guidance follow-up.',
        'You may request deletion of your data at any time by contacting us.',
      ],
    },
    {
      title: '5. Cookies',
      content: [
        'O4S.ai uses local storage (similar to cookies) to remember your login session and certain preferences (such as whether you have completed a lead form).',
        'We may use analytics tools (such as Google Tag Manager) to understand how our platform is used. These tools may set their own cookies.',
        'You can clear your browser storage at any time, though this will log you out and reset certain preferences.',
      ],
    },
    {
      title: '6. Third-Party Services',
      content: [
        'We use OpenAI\'s AI models to generate career recommendations. Your assessment inputs are sent to OpenAI for processing. OpenAI\'s privacy policy applies to that processing.',
        'If you use Google sign-in, Google\'s privacy policy governs the authentication process.',
        'We may use analytics services to understand platform usage. These services have their own privacy policies.',
      ],
    },
    {
      title: '7. Your Rights',
      content: [
        'You have the right to access, correct, or delete your personal data.',
        'You have the right to withdraw consent for data processing at any time.',
        'You may request a copy of the data we hold about you.',
        'To exercise any of these rights, please contact us through our Contact page.',
      ],
    },
    {
      title: '8. Children\'s Privacy',
      content: [
        'O4S.ai is designed for students, which may include minors. We do not knowingly collect sensitive personal data from children under 13 without parental consent.',
        'If you are a parent or guardian and believe your child has provided personal information to us, please contact us and we will take steps to remove such information.',
      ],
    },
    {
      title: '9. Changes to This Policy',
      content: [
        'We may update this Privacy Policy from time to time. Any changes will be reflected on this page with an updated effective date.',
        'We encourage you to review this page periodically to stay informed about how we protect your information.',
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEOHead title="Privacy Policy — O4S.ai" path="/privacy" />

      <nav className="glass-nav sticky top-0 z-50 px-4 md:px-8 py-3 border-b border-earth-200">
        <div className="max-w-4xl mx-auto flex items-center gap-3">
          <Link to="/" className="flex items-center gap-2 text-earth-500 hover:text-earth-700 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <Target className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold text-earth-700">O4S.ai</span>
          </Link>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-4 md:px-8 py-10 md:py-16">
        <div className="flex items-center gap-3 mb-2">
          <Shield className="w-7 h-7 text-primary" />
          <h1 className="text-3xl sm:text-4xl font-bold text-earth-700">Privacy Policy</h1>
        </div>
        <p className="text-earth-500 text-sm mb-10">Last updated: March 2026</p>

        <p className="text-earth-600 leading-relaxed mb-8">At O4S.ai, we take your privacy seriously. This Privacy Policy explains what information we collect, how we use it, and how we protect it. By using our platform, you agree to the practices described here.</p>

        {sections.map((section, i) => (
          <section key={i} className="mb-8">
            <h2 className="text-lg font-bold text-earth-700 mb-3">{section.title}</h2>
            <ul className="space-y-2">
              {section.content.map((item, j) => (
                <li key={j} className="text-earth-600 text-sm leading-relaxed flex gap-2">
                  <span className="text-primary mt-1 flex-shrink-0">-</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </section>
        ))}

        <p className="text-earth-500 text-sm border-t border-earth-200 pt-6">If you have any questions about this Privacy Policy, please visit our <Link to="/contact" className="text-primary underline">Contact page</Link>.</p>
      </main>

      <footer className="px-4 md:px-8 py-8 bg-earth-700 text-white mt-auto">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-wrap justify-center gap-x-5 gap-y-2 text-sm mb-4">
            <Link to="/about" className="text-earth-400 hover:text-white transition-colors">About</Link>
            <Link to="/founder" className="text-earth-400 hover:text-white transition-colors">Founder</Link>
            <Link to="/contact" className="text-earth-400 hover:text-white transition-colors">Contact</Link>
            <Link to="/privacy" className="text-earth-400 hover:text-white transition-colors">Privacy</Link>
            <Link to="/terms" className="text-earth-400 hover:text-white transition-colors">Terms</Link>
            <Link to="/legal" className="text-earth-400 hover:text-white transition-colors">Legal</Link>
          </div>
          <p className="text-earth-500 text-xs text-center">&copy; 2026 O4S.ai. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPage;
